import React, { useState, useEffect } from 'react';
import Button from '@/components/common/Button';
import CustomSelect from '@/components/common/CustomSelect';
import { FaCog, FaPen, FaEnvelope, FaSave } from 'react-icons/fa';
import ValidatedInput from '@/components/common/ValidatedInput';

/**
 * Formulario para crear o editar configuraciones del sistema
 */
const ConfiguracionForm = ({ 
  initialData = {}, 
  onSubmit, 
  onCancel, 
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    clave: '',
    valor: '',
    tipo: 'string',
    descripcion: ''
  });

  // Si hay datos iniciales, actualizar el estado del formulario
  useEffect(() => {
    if (initialData && Object.keys(initialData).length) {
      setFormData({
        clave: initialData.clave || '',
        valor: initialData.valor || '',
        tipo: initialData.tipo || 'string',
        descripcion: initialData.descripcion || ''
      });
    }
  }, [initialData]);

  // Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Manejar envío del formulario
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="text-white">
      <div className="md:grid md:grid-cols-2 md:gap-6">
        {/* Columna izquierda: Información Básica */}
        <div className="bg-gray-800 p-4 rounded-lg mb-6 md:mb-0 border border-gray-600">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <FaCog className="text-blue-400 mr-2" />
            Información Básica
          </h3>
          
          {/* Clave de configuración */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium mb-2">
              <FaCog className="text-gray-400 mr-2 text-xs" />
              Clave de Configuración<span className="text-red-500 ml-1">*</span>
            </label>
            <ValidatedInput
              type="text"
              name="clave"
              value={formData.clave}
              onChange={handleChange}
              placeholder="ej: nombre_empresa, timezone, etc."
              disabled={initialData.id !== undefined}
              required
              requiredMessage='Clave de configuración es requerida'
            />
            {initialData.id && (
              <p className="text-xs text-gray-400 mt-1">La clave no se puede modificar al editar</p>
            )}
          </div>

          {/* Tipo */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium mb-2">
              <FaCog className="text-gray-400 mr-2 text-xs" />
              Tipo
            </label>
            <CustomSelect
              name="tipo"
              value={formData.tipo}
              onChange={handleChange}
              options={[
                { value: 'string', label: 'Texto (string)' },
                { value: 'number', label: 'Número (number)' },
                { value: 'boolean', label: 'Booleano (boolean)' },
                { value: 'json', label: 'JSON (json)' }
              ]}
              className="w-full"
              required
              requiredMessage='Tipo es requerido'
            />
          </div>
        </div>
        
        {/* Columna derecha: Valor y Descripción */}
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-600">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <FaPen className="text-green-400 mr-2" />
            Valor y Descripción
          </h3>
          
          {/* Valor */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium mb-2">
              <FaPen className="text-gray-400 mr-2 text-xs" />
              Valor<span className="text-red-500 ml-1">*</span>
            </label>
            {formData.tipo === 'json' ? (
              <textarea
                name="valor"
                value={formData.valor}
                onChange={handleChange}
                className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors"
                placeholder='ej: {"key": "value"}'
                rows={4}
                required
              />
            ) : (
              <ValidatedInput
                type={formData.tipo === 'number' ? 'number' : 'text'}
                name="valor"
                value={formData.valor}
                onChange={handleChange}
                placeholder="Valor de la configuración"
                required
                requiredMessage='Valor es requerido'
              />
            )}
          </div>

          {/* Descripción */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium mb-2">
              <FaEnvelope className="text-gray-400 mr-2 text-xs" />
              Descripción
            </label>
            <textarea
              name="descripcion"
              value={formData.descripcion}
              onChange={handleChange}
              className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
              placeholder="Descripción de la configuración (opcional)"
              rows={3}
            />
          </div>
        </div>
      </div>
      
      {/* Ayuda contextual */}
      {formData.tipo === 'boolean' && (
        <div className="bg-blue-900/30 rounded-lg border border-blue-800/50 p-3 mb-4">
          <p className="text-sm text-blue-300">
            Para valores booleanos, usar <code className="bg-gray-700 px-1 rounded">true</code> o <code className="bg-gray-700 px-1 rounded">false</code>
          </p>
        </div>
      )}
      
      {formData.tipo === 'json' && (
        <div className="bg-green-900/30 rounded-lg border border-green-800/50 p-3 mb-4">
          <p className="text-sm text-green-300">
            Para valores JSON, asegúrate de usar formato válido: <code className="bg-gray-700 px-1 rounded">{"{"}"clave": "valor"{"}"}</code>
          </p>
        </div>
      )}
      
      {/* Separador visual y botones */}
      <div className="border-t border-gray-600 pt-6">
        <div className="flex justify-end gap-3">
          <Button 
            variant="secondary" 
            type="button" 
            onClick={onCancel}
            className="py-2.5 px-6 text-sm font-medium"
          >
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={loading}
            className="py-2.5 px-6 text-sm font-medium flex items-center gap-2"
          >
            {!loading && <FaSave />}
            {loading ? 'Guardando...' : (initialData.id ? 'Actualizar' : 'Guardar') + ' Configuración'}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ConfiguracionForm;